import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi, clearAdminToken } from '../../services/adminApi';
import { Loader2, ArrowLeft, Search, Activity, AlertTriangle, Info, AlertOctagon } from 'lucide-react';

const SeverityIcon = ({ severity }) => {
  if (severity === 'warning') return <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />;
  if (severity === 'critical') return <AlertOctagon className="w-3.5 h-3.5 text-red-400" />;
  return <Info className="w-3.5 h-3.5 text-muted-foreground" />;
};

const ACTION_PRESETS = [
  { label: 'All actions', value: '' },
  { label: 'Subscription', value: 'subscription' },
  { label: 'IMEI bind/unbind', value: 'imei' },
  { label: 'Asset', value: 'asset' },
  { label: 'Plan changes', value: 'plan' },
];

const AdminAuditLogPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.auditLogs({
        action: actionFilter || undefined,
        severity: severityFilter || undefined,
        limit: 100,
      });
      setRows(r.data?.items || []);
    } catch (e) {
      if (e.response?.status === 401) {
        clearAdminToken();
        navigate('/admin/login', { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [actionFilter, severityFilter, navigate]);

  useEffect(() => { load(); }, [load]);

  const filteredRows = rows.filter((r) =>
    !search ||
    (r.tenant_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (r.user_email || '').toLowerCase().includes(search.toLowerCase()) ||
    (r.action || '').toLowerCase().includes(search.toLowerCase()) ||
    (r.resource_id || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background" data-testid="admin-audit-log-page">
      <header className="border-b border-border bg-card/40 sticky top-0 z-10 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <button
            onClick={() => navigate('/admin/tenants')}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="admin-back-to-tenants"
          >
            <ArrowLeft className="w-4 h-4" /> Tenants
          </button>
          <h1 className="text-base font-bold uppercase tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Audit log
          </h1>
          <div />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <div className="flex items-center gap-3 text-muted-foreground">
          <Activity className="w-4 h-4 text-primary" />
          <p className="text-sm">
            Cross-tenant audit of plan transitions, IMEI binds, asset deletions, and other privileged operations.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by tenant / user / action…"
              className="w-full h-9 pl-9 pr-3 rounded-md bg-card border border-border text-sm"
              data-testid="audit-search"
            />
          </div>
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="h-9 px-3 rounded-md bg-card border border-border text-sm"
            data-testid="audit-action-filter"
          >
            {ACTION_PRESETS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className="h-9 px-3 rounded-md bg-card border border-border text-sm"
            data-testid="audit-severity-filter"
          >
            <option value="">Any severity</option>
            <option value="info">Info</option>
            <option value="warning">Warning</option>
            <option value="critical">Critical</option>
          </select>
          <div className="ml-auto text-xs text-muted-foreground" data-testid="audit-count">
            {loading ? '…' : `${filteredRows.length} event${filteredRows.length === 1 ? '' : 's'}`}
          </div>
        </div>

        {/* Table */}
        <div className="rounded-md border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left p-3 w-44">Timestamp</th>
                <th className="text-left p-3">Action</th>
                <th className="text-left p-3">Tenant</th>
                <th className="text-left p-3">User</th>
                <th className="text-left p-3">Resource</th>
                <th className="text-left p-3">Details</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="p-12 text-center">
                    <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto" />
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-12 text-center text-muted-foreground">
                    No events match the filter.
                  </td>
                </tr>
              ) : (
                filteredRows.map((r) => (
                  <tr key={r.id} className="border-t border-border align-top" data-testid={`audit-row-${r.id}`}>
                    <td className="p-3 font-mono text-xs text-muted-foreground whitespace-nowrap">
                      {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <SeverityIcon severity={r.severity} />
                        <span className="font-mono text-xs">{r.action}</span>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="text-sm">{r.tenant_name || '—'}</div>
                      <div className="text-[10px] font-mono text-muted-foreground/60">{r.tenant_id?.slice(0, 8)}</div>
                    </td>
                    <td className="p-3 text-xs">
                      <div>{r.user_email || <span className="text-muted-foreground italic">system</span>}</div>
                      {r.ip_address && (
                        <div className="text-[10px] font-mono text-muted-foreground/60">{r.ip_address}</div>
                      )}
                    </td>
                    <td className="p-3 text-xs">
                      <div>{r.resource_type || '—'}</div>
                      <div className="text-[10px] font-mono text-muted-foreground/60">{r.resource_id?.slice(0, 12)}</div>
                    </td>
                    <td className="p-3">
                      {r.details && Object.keys(r.details).length > 0 ? (
                        <pre className="text-[10px] font-mono text-muted-foreground bg-black/30 rounded-sm p-2 max-w-md overflow-x-auto">
                          {JSON.stringify(r.details, null, 2)}
                        </pre>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

export default AdminAuditLogPage;
